import type { ComponentProps, ReactNode } from 'react';

import Text from '../base/TextNew';
import Button from '../input/Button';
import Modal from '../molecules/Modal';

type CurrentPlanButtonProps = ComponentProps<typeof Button>;

export interface CurrentPlanFeatureAction {
  label: ReactNode;
  onClick?: CurrentPlanButtonProps['onClick'];
  href?: string;
  icon?: CurrentPlanButtonProps['icon'];
  alignIcon?: CurrentPlanButtonProps['alignIcon'];
  variant?: CurrentPlanButtonProps['variant'];
  size?: CurrentPlanButtonProps['size'];
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  target?: string;
  rel?: string;
}

export interface CurrentPlanFeatureModalProps {
  open: boolean;
  title: string;
  description?: ReactNode;
  onOpenChange: (open: boolean) => void;
  footer: ReactNode;
  children?: ReactNode;
  maxWidth?: number;
}

export interface CurrentPlanFeatureDetailItem {
  label: ReactNode;
  value: ReactNode;
}

interface CurrentPlanFeatureBaseProps {
  supportActions?: CurrentPlanFeatureAction[];
  modal?: CurrentPlanFeatureModalProps;
}

export interface CurrentPlanFeatureLoadingProps {
  state: 'loading';
}

export interface CurrentPlanFeatureStandardProps
  extends CurrentPlanFeatureBaseProps {
  state: 'standard';
  planEyebrow: ReactNode;
  planTitle: ReactNode;
  planMeta?: ReactNode;
  balanceEyebrow: ReactNode;
  balanceValue: ReactNode;
  balanceAccessory?: ReactNode;
  scheduleEyebrow: ReactNode;
  scheduleValue: ReactNode;
  primaryActions?: CurrentPlanFeatureAction[];
}

export interface CurrentPlanFeatureOrgCreditsProps
  extends CurrentPlanFeatureBaseProps {
  state: 'org-credits';
  planEyebrow: ReactNode;
  planTitle: ReactNode;
  planTitleVariant?: 'default' | 'trial';
  planMeta?: ReactNode;
  balanceEyebrow: ReactNode;
  balanceValue: ReactNode;
  balanceIcon?: ReactNode;
  balanceFootnote?: ReactNode;
  detailItems: CurrentPlanFeatureDetailItem[];
  detailNote?: ReactNode;
  primaryActions?: CurrentPlanFeatureAction[];
  detailLayout?: 'stack' | 'split';
}

export type CurrentPlanFeatureProps =
  | CurrentPlanFeatureLoadingProps
  | CurrentPlanFeatureStandardProps
  | CurrentPlanFeatureOrgCreditsProps;

export default function CurrentPlanFeature(props: CurrentPlanFeatureProps) {
  if (props.state === 'loading') {
    return (
      <div className="bg-yellow text-gray-90 animate-pulse rounded-lg p-6">
        <div className="flex flex-col gap-2">
          <div className="bg-gray-90/30 h-4 w-32 rounded" />
          <div className="bg-gray-90/30 h-5 w-48 rounded" />
          <div className="bg-gray-90/30 mt-5 h-4 w-24 rounded" />
          <div className="bg-gray-90/30 h-5 w-32 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="bg-yellow text-gray-90 rounded-2xl p-6">
        {props.state === 'org-credits' ? (
          <div className="flex flex-col gap-8">
            <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-start">
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <Text token="14" className="opacity-60">
                  {props.planEyebrow}
                </Text>
                <Text
                  as="div"
                  token={
                    props.planTitleVariant === 'trial'
                      ? 'display/20/medium'
                      : 'display/16/medium'
                  }
                  className={props.planTitleVariant === 'trial' ? '' : 'mt-1'}
                >
                  {props.planTitle}
                </Text>
                {props.planMeta ? (
                  <Text token="14">{props.planMeta}</Text>
                ) : null}
              </div>

              <div className="flex min-w-0 flex-col items-start gap-5 sm:flex-1">
                <div className="flex items-center gap-1.5 opacity-60">
                  {props.balanceIcon}
                  <Text token="14">{props.balanceEyebrow}</Text>
                </div>
                <div className="flex flex-wrap items-baseline justify-start gap-x-3 gap-y-0.5">
                  <Text as="span" token="display/36/bold">
                    {props.balanceValue}
                  </Text>
                  {props.balanceFootnote}
                </div>
              </div>
            </div>

            <div
              className={
                props.detailLayout === 'split'
                  ? 'grid grid-cols-1 gap-4 sm:grid-cols-2 sm:items-end sm:gap-x-6'
                  : 'flex flex-col gap-4'
              }
            >
              <div className="flex min-w-0 flex-col gap-5">
                {props.detailItems.map((item, index) => (
                  <div key={index} className="flex flex-col gap-2">
                    <Text token="14" className="opacity-60">
                      {item.label}
                    </Text>
                    <Text token="14">{item.value}</Text>
                  </div>
                ))}
                {props.detailNote ? (
                  <Text token="14" className="max-w-md opacity-60">
                    {props.detailNote}
                  </Text>
                ) : null}
              </div>

              {props.primaryActions && props.primaryActions.length > 0 ? (
                <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:gap-2">
                  {props.primaryActions.map((action, index) => {
                    const className = [
                      'w-full min-w-0 flex-1',
                      action.className,
                    ]
                      .filter(Boolean)
                      .join(' ');
                    const button = (
                      <Button
                        variant={action.variant}
                        size={action.size}
                        disabled={action.disabled}
                        loading={action.loading}
                        icon={action.icon}
                        alignIcon={action.alignIcon}
                        onClick={action.onClick}
                        className={className}
                      >
                        {action.label}
                      </Button>
                    );

                    return action.href ? (
                      <a
                        key={index}
                        href={action.href}
                        target={action.target}
                        rel={action.rel}
                        style={{ textDecoration: 'none' }}
                        className="w-full min-w-0 flex-1"
                      >
                        {button}
                      </a>
                    ) : (
                      <div key={index} className="w-full min-w-0 flex-1">
                        {button}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-start">
              <div className="flex min-w-0 flex-1 flex-col gap-3">
                <Text token="14" className="opacity-60">
                  {props.planEyebrow} 2
                </Text>
                <Text as="p" token="display/16/medium">
                  {props.planTitle}
                </Text>
                {props.planMeta ? (
                  <Text token="14" className="opacity-60">
                    {props.planMeta}
                  </Text>
                ) : null}
              </div>

              <div className="flex flex-col items-start gap-2 sm:items-end sm:text-right">
                <Text token="14" className="opacity-60">
                  {props.balanceEyebrow}
                </Text>
                <Text as="p" token="display/36/bold">
                  {props.balanceValue}
                  {props.balanceAccessory}
                </Text>
              </div>
            </div>

            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="flex flex-col gap-3">
                <Text token="14" className="opacity-60">
                  {props.scheduleEyebrow}
                </Text>
                <Text token="14">{props.scheduleValue}</Text>
              </div>

              {props.primaryActions && props.primaryActions.length > 0 ? (
                <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:justify-end">
                  {props.primaryActions.map((action, index) => {
                    const className = ['w-full sm:w-auto', action.className]
                      .filter(Boolean)
                      .join(' ');
                    const button = (
                      <Button
                        variant={action.variant}
                        size={action.size}
                        disabled={action.disabled}
                        loading={action.loading}
                        icon={action.icon}
                        alignIcon={action.alignIcon}
                        onClick={action.onClick}
                        className={className}
                      >
                        {action.label}
                      </Button>
                    );

                    return action.href ? (
                      <a
                        key={index}
                        href={action.href}
                        target={action.target}
                        rel={action.rel}
                        style={{ textDecoration: 'none' }}
                      >
                        {button}
                      </a>
                    ) : (
                      <div key={index} className="flex">
                        {button}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {props.supportActions && props.supportActions.length > 0 ? (
        <div className="flex flex-col gap-2 md:flex-row">
          {props.supportActions.map((action, index) => {
            const className = ['w-full', action.className]
              .filter(Boolean)
              .join(' ');
            const button = (
              <Button
                variant={action.variant}
                size={action.size}
                disabled={action.disabled}
                loading={action.loading}
                icon={action.icon}
                alignIcon={action.alignIcon}
                onClick={action.onClick}
                className={className}
              >
                {action.label}
              </Button>
            );

            return action.href ? (
              <a
                key={index}
                href={action.href}
                target={action.target}
                rel={action.rel}
                style={{ textDecoration: 'none' }}
                className="flex flex-1"
              >
                {button}
              </a>
            ) : (
              <div key={index} className="flex flex-1">
                {button}
              </div>
            );
          })}
        </div>
      ) : null}

      {props.modal ? (
        <Modal
          open={props.modal.open}
          onOpenChange={props.modal.onOpenChange}
          title={props.modal.title}
          description={props.modal.description}
          maxWidth={props.modal.maxWidth ?? 440}
          footer={props.modal.footer}
        >
          {props.modal.children ?? null}
        </Modal>
      ) : null}
    </div>
  );
}
